<script>
/**
 * The Scene class acts as the container instance to support the 
 * TBS Scene plugin for the scene view. It introduces the 
 * scene-based development pattern for the whole application
 * or scoped view.
 */

//--------------------------------------------------
// imported classes
//--------------------------------------------------
import Vue from 'vue'
import { Tabs, Tab, Icon, Row, Col, Loading, Overlay, Toast } from 'vant'
import PickerPopup from '@/fcl/components/user/PickerPopup'
import ServicePopup from '@/fcl/components/user/ServicePopup'
import AbastractScene from '@/fcl/tbs/core/AbstractScene.vue'
import { QNImageUtil } from '@/fcl/utils'

//--------------------------------------------------
// global registration
//--------------------------------------------------
Vue.use(Loading).use(Overlay)

//--------------------------------------------------
// main script
//--------------------------------------------------
export default {
    //--------------------------------------------------
	// component extends
	//--------------------------------------------------
    extends: AbastractScene,

    //--------------------------------------------------
	// component components
	//--------------------------------------------------
    components: {
        'van-tabs': Tabs,
        'van-tab': Tab,
        'van-icon': Icon,
        'van-row': Row,
        'van-col': Col,
        'picker-popup': PickerPopup,
        'service-popup': ServicePopup,
    },

    //--------------------------------------------------
    // component data
    //--------------------------------------------------
	data() {
        return {
            player: {
                fact: {},
                selections: [],
                season: {},
                summary: {},
                list: [],
                product: {}
            },
            activeName: null,  // use the active name
            isExpanded: false,
            showPickerPopup: false,
            showServicePopup: false,
            locale: {}
        }
	},

    //--------------------------------------------------
    // component life cycle trigger
    //--------------------------------------------------

    //--------------------------------------------------
    // computed setter and getter
    //--------------------------------------------------
    computed: {
    },

    //--------------------------------------------------
    // component methods
    //--------------------------------------------------
    methods: {
        //--------------------------------------------------
        // handler method
        //--------------------------------------------------
        changeTabName: function(name) {
            this.onParentEvent({
                type: 'navigate',
                data: {
                    name: name,
                    query: {
                        playerId: this.player.fact.id,
                        role: this.player.fact.role,
                    },
                    isReplace: true
                }
            })
        },
        onTabsChanged: function(name) {
            // console.log('onTabsChanged', name)
            this.changeTabName(name)
        },
        onSwitchBtnClick: function() {
            // console.log('onSwitchBtnClick')
            if (!this.showPickerPopup) {
                this.showPickerPopup = true
            }
        },
        onPlayerDataChange: function(data) {
            // console.log('onPlayerDataChange', data)
            this.showPickerPopup = false
            if (data.value) {
                this.onParentEvent({
                    type: 'navigate',
                    data: {
                        name: 'PlayerStatsReport',
                        query: {
                            playerId: data.value,
                            role: data.role,
                            season: '',
                            league: '',
                            group: ''
                        },
                        isReplace: true,
                        reload: true
                    }
                })
            }
        },
        popupService: function() {
            if (this.player.summary.game == 0) {
                Toast(this.locale['none-valid-game-stats'])
                return
            }
            if (!this.showServicePopup) {
                this.showServicePopup = true
            }
        },
        onServiceDataChange: function(data) {
             console.log('onServiceDataChange', data)
            this.showServicePopup = false
            if (data) {
                this.onParentEvent({
                    type: 'order-service',
                    data: {
                        playerId: this.player.fact.id,
                        playerName: this.player.fact.name
                    }
                })
            }
        },

        //--------------------------------------------------
        // private method
        //--------------------------------------------------
        /**
         * @private
         * Adjust the image url according to the width and height.
         * @param img The identifier string for the image source.
         * @param w The image width number.
         * @param h The image height number.
         * @return The completed full url string.
         */
		adjustImageSize(img, w, h, mode) {
            // console.log('adjustImageSize', img, w, h, mode)
            if (img) {
                return QNImageUtil.imageView2(img, w, h, mode)
            }
			else {
                return null
            }
		},
    },
}
</script>

<template>
    <!-- component -->
    <div class="scene-wrapper">
        <div class="scene none-select">
            <div class="head">
                 <div class="basic-info color-white l-wide-margin r-wide-margin">
                    <div class="profile vertical-center t-outer-padding b-wide-margin">
                        <div class="avatar-wrapper inline-block r-outer-margin">
                            <img
                                v-if="player.fact.avatar"
                                class="corner-round-md"
                                :src="adjustImageSize(player.fact.avatar, 220, 292)"
                                style="width: 110px; height: 146px;"
                            />
                            <img v-else
                                class="corner-round-md"
                                src="@/fcl/assets/user/avatar_placeholder_rectangle.png"
                                style="width: 110px; height: 146px;"
                            />
                        </div>
                        <div class="info-block inline-block">
                            <span class="block b-inner-margin vertical-center">
                                <span class="large-title r-inner-margin">{{ player.fact.name }}</span>
                                <span class="gender inline-block corner-round text-center">
                                    <van-icon
                                        v-if="player.fact.gender == 1"
                                        class-prefix="iconfont"
                                        name="man"
                                        size="12"
                                    />
                                    <van-icon
                                        v-if="player.fact.gender == 2"
                                        class-prefix="iconfont"
                                        name="female"
                                        size="12"
                                    />
                                </span>
                            </span>
                            <span class="block large-title b-inner-margin">#{{ player.fact.playerNo }}</span>
                            <span class="block minor-text">{{ player.season.seasonPeriod }}</span>
                            <span class="block minor-text">{{ player.fact.position }} / {{ player.fact.sc }}</span>
                            <span class="block minor-text">{{ locale['height'] }} {{ player.fact.height }} / {{ locale['weight'] }} {{ player.fact.weight }}</span>
                            <span class="block minor-text">{{ locale['age'] }} {{ player.fact.age }} / {{ locale['birthday'] }} {{ player.fact.birthday }}</span>
                        </div>
                        <div v-if="player.list.length > 1" class="player-selector inline-block">
                            <div class="absolute" style="right: 20px; top: 20px;">
                                <van-icon
                                    class-prefix="iconfont"
                                    name="switch"
                                    size="22"
                                    @click="onSwitchBtnClick"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="owner-unit">
                        <div v-if="player.fact.organizationName">
                            <van-icon
                                class-prefix="iconfont"
                                name="icon-company"
                                size="12"
                            />
                            <span class="l-inner-margin minor-text">{{ player.fact.organizationName }}</span>
                        </div>
                        <div v-if="player.fact.school">
                            <van-icon
                                class-prefix="iconfont"
                                name="icon-school"
                                size="12"
                            />
                            <span class="l-inner-margin minor-text">{{ player.fact.school }}</span>
                        </div>
                    </div>
                    <div v-if="player.selections.length > 0" class="selection t-outer-margin">
                        <div class="bar corner-round-sm w-inner-padding vertical-center">
                            <div 
                                v-if="!isExpanded" 
                                class="minor-text wp100"
                                @click="isExpanded = true"
                            >
                                <span>{{ locale['player-selections'] }}</span>
                                <span class="pull-right">
                                    <van-icon
                                        class-prefix="iconfont"
                                        name="icon-arrow-down"
                                        size="12"
                                    />
                                </span>
                                <span class="minor-color pull-right r-inner-margin">{{ locale['expand'] }}</span>
                            </div>
                            <div 
                                v-else 
                                class="minor-text wp100"
                                @click="isExpanded = false"
                            >
                                <span>{{ locale['player-selections'] }}</span>
                                <span class="pull-right">
                                    <van-icon
                                        class-prefix="iconfont"
                                        name="icon-arrow-up"
                                        size="12"
                                    />
                                </span>
                                <span class="minor-color pull-right r-inner-margin">{{ locale['collapse'] }}</span>
                            </div>
                        </div>
                        <div v-if="isExpanded" class="body t-inner-margin b-inner-padding">
                            <van-row class="minor-text minor-color text-center u-square-padding">
                                <van-col span="6">
                                    <span>{{ locale['level-label'] }}</span>
                                </van-col>
                                <van-col span="6">
                                    <span>{{ locale['season-label'] }}</span>
                                </van-col>
                                <van-col span="12">
                                    <span>{{ locale['name-label'] }}</span>
                                </van-col>
                            </van-row>
                            <van-row 
                                v-for="(item, index) in player.selections" 
                                :key="index"
                                class="minor-text text-center u-narrow-padding">
                                <van-col span="6">
                                    <span>{{ item.level }}</span>
                                </van-col>
                                <van-col span="6">
                                    <span>{{ item.season }}</span>
                                </van-col>
                                <van-col span="12">
                                    <span>{{ item.name }}</span>
                                </van-col>
                            </van-row>
                        </div>
                    </div>
                </div>
                <div class="stats-info color-white ">
                    <van-row class="text-center h-inner-padding">
                        <van-col span="8">
                            <span class="block large-title">{{ player.summary.season }}</span>
                            <span class="block support-text">{{ locale['season-total'] }}</span>
                        </van-col>
                        <van-col span="8">
                            <span class="block large-title">{{ player.summary.match }}</span>
                            <span class="block support-text">{{ locale['match-total'] }}</span>
                        </van-col>
                        <van-col span="8">
                            <span class="block large-title">{{ player.summary.game }}</span>
                            <span class="block support-text">{{ locale['game-total'] }}</span>
                        </van-col>
                    </van-row>
                </div>
            </div>
            <div class="body">
                <van-tabs 
                    class="category-tabs bg-white"
                    v-model="activeName" 
                    @change="onTabsChanged"
                >
                    <van-tab :title="locale['player-summary']" name="PlayerStatsReportSummary"/>
                    <van-tab :title="locale['player-stats']" name="PlayerStatsReportStats"/>
                    <van-tab :title="locale['player-games']" name="PlayerStatsReportGames"/>
                </van-tabs>
                <!-- router view as scene view -->
                <router-view
                    class="scene-view bg-white"
                    ref="sceneView" 
                    :key="$route.name"
                    @custom-event="onCustomEvent" 
                />
            </div>
            <!-- player picker popup -->
            <picker-popup
                :label="locale['select-player']"
                :options="player.list"
                :popup="showPickerPopup"
                @change-data="onPlayerDataChange"
            />
            <service-popup
                :popup="showServicePopup"
                :tag="player.product.tag"
                :price="player.product.price"
                @change-data="onServiceDataChange"
            />
        </div>
    </div>
</template>

<style src="../../fcl/tbs/stylesheet/common.css"/>
<style src="../../fcl/tbs/stylesheet/iconfont.css"/>
<style scoped>
 .scene {
    background-color: #FFF;
}
.head,
.body {
    background-color: #0D3978
}
.gender {
    width: 18px;
    height: 18px;
    background: rgba(255, 255, 255, .2);
}
.selection .bar {
    height: 27px;
    border: 1px solid rgba(255, 255, 255, .2); 
}
.selection .body {
    background-color: rgba(0,0,0,.1);
}
.category-tabs {
    border-radius: 20px 20px 0px 0px;
}
</style>
<style>
.van-tabs__line {
    background-color: #0D3978 !important;
    width: 67px;
}
.van-tab--active {
    color: #1D2129 !important;
    font-size: 17px !important;
}
.van-tab {
    color: #8E9196;
}
</style>